import {Component, EventEmitter, Input, Output} from '@angular/core';
import {PreFilterKeysModel} from "../../api/models/pre-filter-keys-model";
import {MatTreeFlatDataSource, MatTreeFlattener} from "@angular/material/tree";
import {PreFilterHierarchyKeyValuesModel} from "../../api/models/pre-filter-hierarchy-key-values-model";
import {FlatTreeControl} from "@angular/cdk/tree";
import {SelectionModel} from "@angular/cdk/collections";

export class TreeNode {
  children?: TreeNode[];
  item: string;
}

export class FlatTreeNode {
  expandable: boolean;
  item: string;
  level: number;
}

@Component({
  selector: 'app-pre-filter-hierarchy-editor',
  templateUrl: './pre-filter-hierarchy-editor.component.html',
  styleUrls: ['./pre-filter-hierarchy-editor.component.css']
})
export class PreFilterHierarchyEditorComponent {

  @Input() title: string = 'Select Options';
  @Input() maxHeight: number = 200;
  @Input() allowSelect: boolean = false;

  @Input() set preFilterKey(availableValues: PreFilterKeysModel) {
    this.dataSource.data = this._loadData(availableValues.hierarchyValues);
  }

  @Input() set selectedValues(values: string[]) {
    this.currentSelections.clear();

    values.forEach(value => {
      const matchingNodes = this.treeControl.dataNodes.filter(x => x.item === value);
      if(matchingNodes?.length > 0) {
        matchingNodes.forEach(x => {
          this.currentSelections.select(x);
        })
      }
    });
  }

  currentSelections = new SelectionModel<FlatTreeNode>(true /* multiple */);
  @Output() currentSelectedValues = new EventEmitter<string[]>();

  public dropDownShown: boolean = false;

  constructor() { }

  private _loadData = (preFilterValues: PreFilterHierarchyKeyValuesModel[]): TreeNode[] => {
    return preFilterValues.map((preFilterValue: PreFilterHierarchyKeyValuesModel): TreeNode => {
      return {
        item: preFilterValue.keyValue,
        children: preFilterValue.childValues ? this._loadData(preFilterValue.childValues) : []
      };
    });
  }

  private _transformer = (node: TreeNode, level: number) : FlatTreeNode => {
    return {
      expandable: node.children.length > 0,
      item: node.item,
      level: level
    }
  }

  treeControl = new FlatTreeControl<FlatTreeNode>(
    node => node.level, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    this._transformer, node => node.level, node => node.expandable, node => node.children);

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, _nodeData: FlatTreeNode) => _nodeData.expandable;

  descendantsAllSelected(node: FlatTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.length > 0 && descendants.every(child => {
      return this.currentSelections.isSelected(child);
    });
  }

  descendantsPartiallySelected(node: FlatTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.currentSelections.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  nodeIsSelected(node: FlatTreeNode): boolean {
    return this.currentSelections.isSelected(node);
  }

  toggleSelection(node: FlatTreeNode): void {
    this.currentSelections.toggle(node);
    this.setSelectionRecursive(node, this.currentSelections.isSelected(node));
    this.currentSelectedValues.emit(this.currentSelections.selected.map(x => x.item));
  }

  setSelectionRecursive(node: FlatTreeNode, isSelected: boolean) {
    const descendants = this.treeControl.getDescendants(node);

    if (isSelected) {
      this.currentSelections.select(...descendants);
    } else {
      this.currentSelections.deselect(...descendants);
    }

    descendants.forEach(x => this.setSelectionRecursive(x, isSelected));
  }

  dropDownClicked() {
    this.dropDownShown = !this.dropDownShown;
  }
}
